import { safeId } from '../utils/safeId'

/**
 * Seed data derived from "Grim Execution Sheet.xlsx":
 * - Goals: Professional/Private × Yearly/Monthly/Weekly
 * - Weekdays: Mon–Fri
 * - Timebox types and default layout (30-min slots, 8–17)
 */

export const TIMEBOX_TYPES = [
  { id: 'plan-day', label: 'Plan Day', color: '#5c6bc0' },
  { id: 'deep-work', label: 'Deep Work', color: '#2e7d32' },
  { id: 'to-do-list', label: 'To Do List', color: '#1565c0' },
  { id: 'top-goal', label: 'Top goal of today', color: '#c9a227' },
  { id: 'meetings', label: 'Meetings', color: '#6a1b9a' },
  { id: 'workout', label: 'Workout', color: '#d84315' },
  { id: 'eat', label: 'Eat', color: '#ef6c00' },
  { id: 'emails', label: 'Emails', color: '#00838f' },
  { id: 'walk', label: 'Walk', color: '#2e7d32' },
  { id: 'messenger', label: 'Messenger', color: '#0277bd' },
  { id: 'silent-day', label: 'Silent Day', color: '#37474f' },
  { id: 'morning-habit', label: 'Morning Habit', color: '#ff8f00' },
  { id: 'evening-habit', label: 'Evening Habit', color: '#5e35b1' },
]

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

/** 30-minute slot index: 0 = 00:00, 16 = 08:00, 34 = 17:00 */
export const SLOTS_PER_DAY = 48
export const FIRST_SLOT = 16   // 08:00
export const LAST_SLOT = 34    // 17:00

// Time-of-day areas derived from the Excel template:
// - Morning: 08:00–12:00 (focus on Plan Day, Deep Work, Eat, Walk)
// - Midday: 12:00–13:00 (Eat, Walk, To Do List)
// - Afternoon: 13:00–17:00 (To Do List, Meetings, Workout, Eat, Emails, Messenger)
export const TIME_AREAS = [
  {
    id: 'morning',
    label: 'Morning',
    startSlot: 16, // 08:00
    endSlot: 23,   // 11:30
    typeIds: ['plan-day', 'deep-work', 'top-goal', 'eat', 'walk', 'morning-habit'],
  },
  {
    id: 'midday',
    label: 'Midday reset',
    startSlot: 24, // 12:00
    endSlot: 27,   // 13:30
    typeIds: ['eat', 'walk', 'to-do-list', 'silent-day'],
  },
  {
    id: 'afternoon',
    label: 'Afternoon',
    startSlot: 28, // 14:00
    endSlot: 34,   // 17:00
    typeIds: ['to-do-list', 'meetings', 'workout', 'eat', 'emails', 'messenger', 'evening-habit'],
  },
]

export function slotToTime(slot) {
  const h = Math.floor(slot / 2)
  const m = (slot % 2) * 30
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function timeToSlot(timeStr) {
  const [h, m] = timeStr.split(':').map(Number)
  return (h || 0) * 2 + ((m || 0) >= 30 ? 1 : 0)
}

/** Default timeboxes per day from Excel (8–17, 30-min blocks). Same type adjacent = one block. */
function defaultTimeboxesForDay() {
  const raw = [
    { typeId: 'plan-day', slots: 1 },
    { typeId: 'deep-work', slots: 3 },
    { typeId: 'eat', slots: 1 },
    { typeId: 'walk', slots: 1 },
    { typeId: 'to-do-list', slots: 1 },
    { typeId: 'deep-work', slots: 2 },
    { typeId: 'meetings', slots: 1 },
    { typeId: 'workout', slots: 1 },
    { typeId: 'eat', slots: 1 },
    { typeId: 'emails', slots: 1 },
    { typeId: 'messenger', slots: 1 },
  ]
  let start = FIRST_SLOT
  return raw.map(({ typeId, slots }) => {
    const block = { id: safeId(), typeId, startSlot: start, lengthSlots: slots }
    start += slots
    return block
  })
}

export function createInitialState() {
  return {
    goals: {
      professionalYearly: '',
      privateYearly: '',
      professionalMonthly: '',
      privateMonthly: '',
      professionalWeekly: '',
      privateWeekly: '',
    },
    days: Object.fromEntries(
      WEEKDAYS.map(d => [d, { timeboxes: defaultTimeboxesForDay(), locked: false, dayGoal: '' }])
    ),
    timeboxTypes: TIMEBOX_TYPES,
  }
}

export function mergeAdjacentTimeboxes(timeboxes) {
  if (timeboxes.length <= 1) return timeboxes
  const out = []
  let cur = { ...timeboxes[0], lengthSlots: timeboxes[0].lengthSlots ?? 1 }
  for (let i = 1; i < timeboxes.length; i++) {
    const next = timeboxes[i]
    const adjacent = (cur.startSlot + (cur.lengthSlots ?? 1)) === next.startSlot
    const sameType = next.typeId === cur.typeId
    const rightNoAutoMerge = next.noAutoMergeWithPrev === true
    const leftNoAutoMerge = cur.noAutoMergeWithPrev === true
    if (sameType && adjacent && !rightNoAutoMerge && !leftNoAutoMerge) {
      cur = { ...cur, lengthSlots: (cur.lengthSlots ?? 1) + (next.lengthSlots ?? 1), noAutoMergeWithPrev: false }
    } else {
      out.push(cur)
      cur = { ...next, lengthSlots: next.lengthSlots ?? 1 }
    }
  }
  out.push(cur)
  return out
}

/** Normalize day timeboxes: sort by startSlot, merge same-type adjacent, no overlap/gaps in used range. */
export function normalizeDayTimeboxes(timeboxes) {
  const sorted = [...timeboxes].sort((a, b) => a.startSlot - b.startSlot)
  return mergeAdjacentTimeboxes(sorted)
}
